// src/pages/PagesOrders.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Card } from '../../components/card/card.jsx';
import Chart from "react-apexcharts";
import Pagination from "../../components/pagination/Pagination.jsx";
import { getPeriodRange } from './PeriodHelper.js';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  // Legacy (US)
  fetchOrders,
  fetchOrdersComparison,
  setOrdersDateRange,
  setOrdersItemsPerPage,
  setOrdersPageCurrent,

  // Saleor (UK/India)
  fetchChannelOrders,
  fetchChannelOrdersComparison,
  setChannelOrdersCurrency,

  // NEW: analytics products options for multi-select
  fetchAnalyticsProductOptions,
} from "../../actions/orders_actions";

import {
  // SHAKERPRO (shakerpro.barsys.com) — orders are merged into the US channel
  // by the backend; only the fulfillment write-back lives here.
  fulfillShakerproOrder,
  fulfillSaleorUsOrder,
} from "../../actions/shakerpro_orders_actions";

// SHOPMY — creator-attributed orders, an attribution lens over barsys.com
// orders (backend keeps it out of US totals; see engine repo shopmyOrders.model).
import { fetchShopmyOrders } from "../../actions/shopmy_orders_actions";

// ENGINE-NATIVE orders (checkout_id set, external_ref null) do not exist in Saleor,
// so the SALEOR_US fulfil path cannot find them. They go through the engine's own
// admin mutations instead — fulfil AND attach tracking, since the tracking step is
// what emails the customer and releases the PayPal hold.
import { fulfillAndTrack } from "../../actions/commerce_admin_actions";

// SOURCE column — resolves the real origin of a purchase (order.sourceKey, the
// commerce `orders.source` domain) with a graceful fallback to the legacy
// per-producer `order.channel` literal. See orderSource.js for the full rationale.
import { resolveOrderSource } from "./orderSource";

  const formatDbDateNoTz = (value) => {
    if (!value) return "";

    // Expecting "YYYY-MM-DD HH:MM:SS" or similar
    const s = String(value).trim();
    const [datePart, timePart = "00:00:00"] = s.split(" ");

    const [year, month, day] = datePart.split("-").map(Number);
    const [hour = 0, minute = 0, second = 0] = timePart.split(":").map(Number);

    // Create a date in UTC with those exact components
    const d = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

    // Format in user's locale, but **fixed** to UTC so the time doesn't shift
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "UTC", // critical: keeps "07:25:35" as-is
    }).format(d);
  };

// Period presets
const PERIODS = [
  { label: "Today", key: "Today" },
  { label: "Yesterday", key: "Yesterday" },
  { label: "7 days", key: "7d" },
  { label: "30 days", key: "30d" },
  { label: "3 months", key: "3m" },
  { label: "6 months", key: "6m" },
  { label: "12 months", key: "12m" },
];

const COMPARE_RANGES = [
  { label: "Day",   value: "day" },
  { label: "Week",  value: "week" },
  { label: "Month", value: "month" },
  { label: "Year",  value: "year" },
];

const DEFAULT_PERIOD = "3m";

function PagesOrders() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

    const normalizeChannel = (v) => {
    if (!v) return 'US';
    switch (String(v).toLowerCase()) {
      case 'us': return 'US';
      case 'uk': return 'UK';
      case 'india': return 'India';
      case 'shakerpro': return 'US'; // shakerpro orders are merged into the US channel
      case 'shopmy': return 'ShopMy'; // attribution lens — its own view, never merged
      default: return 'US';
    }
  };

  const skipNextSearchResetRef = useRef(false);
  const hydratedRef = useRef(false);

  // ---------------- UI state ----------------
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  // Fulfillment tab filter. null = All. Sent to the backend as ?fulfillmentStatus=…
  // and filtered SERVER-SIDE across all four order sources — never client-side,
  // because the list is paginated: filtering one fetched page yields short pages and
  // wrong totals. Contract: engine repo, docs/handoff-dashboard-ng-fulfillment-filter.md
  // (values Fulfilled | Unfulfilled | Cancelled | Unknown; invalid values are a 400).
  const [fulfillmentFilter, setFulfillmentFilter] = useState(null);
  const [activePeriod, setActivePeriod] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("period") || DEFAULT_PERIOD;
  });

  const [channel, setChannel] = useState(() => {
   const p = new URLSearchParams(window.location.search).get('channel');
   return normalizeChannel(p);
  }); // "US" | "UK" | "India"
  
  // SHAKERPRO fulfillment ("Mark shipped") modal state
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [fulfillTracking, setFulfillTracking] = useState("");
  const [fulfillCarrier, setFulfillCarrier] = useState("UPS");
  const [fulfillError, setFulfillError] = useState(null);

  const [compareRange, setCompareRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const cmp = params.get('compare');
    // default to month if nothing in URL
    return cmp || "month";
  });

  // Safely compute theme color (avoid crashing if CSS var isn't present yet)
  // Resolve theme color AFTER mount to avoid first‐paint fallback
  const themeColor =
    (typeof document !== "undefined"
      ? getComputedStyle(document.body)
          .getPropertyValue("--bs-theme")
          .trim()
      : "") || "#0d6efd";

  // de-dupe refs to avoid duplicate calls (StrictMode, rapid changes, etc.)
  const lastCompareKeyRef = useRef("");
  const lastTableKeyRef = useRef("");

  // ---------------- Redux state ----------------
  // US (legacy) selectors
  const {
    orders, totalOrders, loading_orders, orders_error,
    orders_page_current, orders_items_per_page, orders_date_start, orders_date_end,

    comparison, loadingComparison, comparisonError,
  } = useSelector(state => ({
    orders: state.orders.orders,
    totalOrders: state.orders.totalOrders,
    loading_orders: state.orders.loading_orders,
    orders_error: state.orders.orders_error,
    orders_page_current: state.orders.orders_page_current || 1,
    orders_items_per_page: state.orders.orders_items_per_page || 10,
    orders_date_start: state.orders.orders_date_start,
    orders_date_end: state.orders.orders_date_end,

    comparison: state.orders.comparison,
    loadingComparison: state.orders.loadingComparison,
    comparisonError: state.orders.comparisonError,
  }));

  // Saleor selectors
  const {
    channel_orders,
    channel_totalOrders,
    loading_channel_orders,
    channel_orders_error,

    channel_comparison,
    loadingChannelComparison,
    channelComparisonError,

    channel_orders_currency = "INR",
  } = useSelector(state => state.orders);

  // SHAKERPRO fulfillment state (rows come merged into the US channel)
  const { fulfilling_shakerpro_order } = useSelector(state => state.orders);

  // SHOPMY channel state
  const {
    shopmy_orders = [],
    shopmy_totalOrders = 0,
    loading_shopmy_orders = false,
    shopmy_orders_error = null,
  } = useSelector(state => state.orders);

  const isShopmy = channel === "ShopMy";
  const isChannel = channel !== "US" && !isShopmy;
  const selectedCurrency = channel === "UK" ? "GBP" : channel === "India" ? "INR" : null;

  // List Box selector
  const {
    analytics_product_options,
    loading_analytics_product_options,
    analytics_product_options_error,
  } = useSelector((state) => ({
    analytics_product_options: state.orders.analytics_product_options || [],
    loading_analytics_product_options: state.orders.loading_analytics_product_options,
    analytics_product_options_error: state.orders.analytics_product_options_error,
  }));

  // Multi-select product filter
  const [selectedProductIds, setSelectedProductIds] = useState([]); // current (edited) selection
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAnalyticsProductOptions());
  }, [dispatch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.("#productsMultiSelectDropdown")) {
        setProductsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggleProductId = (id) => {
    const sid = String(id);
    setSelectedProductIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const clearSelectedProducts = () => setSelectedProductIds([]);

  const selectedProductsLabel = useMemo(() => {
    if (!selectedProductIds.length) return "All products";

    const labelById = new Map(
      (analytics_product_options || []).map((o) => [String(o.value), o.label])
    );

    const labels = selectedProductIds.map((id) => labelById.get(String(id)) || id);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  }, [selectedProductIds, analytics_product_options]);



  // NEW: last-applied selection (what charts are currently using)
  const [appliedProductIds, setAppliedProductIds] = useState([]);

  // stable compare helpers
  const normalizeIdsKey = useCallback(
    (ids) => (Array.isArray(ids) ? ids.map(String).filter(Boolean).slice().sort().join(",") : ""),
    []
  );

  const hasSelectionChanges = useMemo(() => {
    return normalizeIdsKey(selectedProductIds) !== normalizeIdsKey(appliedProductIds);
  }, [selectedProductIds, appliedProductIds, normalizeIdsKey]);

  // Build lookup: productId -> upc[]
  const upcsByProductId = useMemo(
    () =>
      new Map(
        (analytics_product_options || []).map((opt) => [
          String(opt.value),
          Array.isArray(opt.upcs) ? opt.upcs.map(String) : [],
        ])
      ),
    [analytics_product_options]
  );

    // NEW: productId -> SKUs (for Saleor / India, UK)
  const skusByProductId = useMemo(
    () =>
      new Map(
        (analytics_product_options || []).map((opt) => [
          String(opt.value),
          Array.isArray(opt.skus) ? opt.skus.map(String) : [],
        ])
      ),
    [analytics_product_options]
  );

  const appliedUpcs = useMemo(() => {
    if (channel !== "US") return [];
    return Array.from(
      new Set(
        (appliedProductIds || [])
          .flatMap((id) => upcsByProductId.get(String(id)) || [])
          .map((s) => String(s).trim())
          .filter(Boolean)
      )
    );
  }, [channel, appliedProductIds, upcsByProductId]);

    // NEW: SKUs for Saleor (India/UK)
  const appliedSkus = useMemo(() => {
    if (channel === "US") return [];
    return Array.from(
      new Set(
        (appliedProductIds || [])
          .flatMap((id) => skusByProductId.get(String(id)) || [])
          .map((s) => String(s).trim())
          .filter(Boolean)
      )
    );
  }, [channel, appliedProductIds, skusByProductId]);


  const handleProductsQueryClick = useCallback(() => {
    setAppliedProductIds(selectedProductIds);
    setProductsDropdownOpen(false);

    
    dispatch(setOrdersPageCurrent(1));      // optional UX
    lastCompareKeyRef.current = "";
    lastTableKeyRef.current = "";
  }, [dispatch, selectedProductIds]);





  // Bind table/comparison UI to selected channel
  const tableLoading = isShopmy ? loading_shopmy_orders : isChannel ? loading_channel_orders   : loading_orders;
  const tableError   = isShopmy ? shopmy_orders_error   : isChannel ? channel_orders_error     : orders_error;
  const tableOrders  = isShopmy ? shopmy_orders         : isChannel ? channel_orders           : orders;
  const tableTotal   = isShopmy ? shopmy_totalOrders    : isChannel ? channel_totalOrders      : totalOrders;

  const cmpLoading   = isChannel ? loadingChannelComparison : loadingComparison;
  const cmpError     = isChannel ? channelComparisonError   : comparisonError;
  const cmpData      = isChannel ? channel_comparison       : comparison;

    // Derive period → dates (prefer Redux dates if present)
  const { startTime: periodStart, endTime: periodEnd } = getPeriodRange(activePeriod);
  const effectiveStart = orders_date_start || periodStart;
  const effectiveEnd   = orders_date_end   || periodEnd;

  // Helpers
  const lighten = (hex, amt) => {
    const h = (hex || '').replace(/^#/, '');
    const num = parseInt(h || '000000', 16);
    let r = (num >> 16) & 0xFF;
    let g = (num >>  8) & 0xFF;
    let b = num & 0xFF;
    r = Math.round(r + (255 - r) * amt);
    g = Math.round(g + (255 - g) * amt);
    b = Math.round(b + (255 - b) * amt);
    const n = ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
    return `#${n}`;
  };

  const safeNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatCurrencyCompact = (value) => {
    const n = safeNumber(value);
    const abs = Math.abs(n);

    if (abs >= 1_000_000_000) {
      return `${currencySymbol}${(n / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "")}B`;
    }
    if (abs >= 1_000_000) {
      return `${currencySymbol}${(n / 1_000_000).toFixed(3).replace(/\.?0+$/, "")}M`;
    }
    if (abs >= 1_000) {
      return `${currencySymbol}${(n / 1_000).toFixed(1).replace(/\.?0+$/, "")}k`;
    }
    return `${currencySymbol}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatNumberAbbrev = (value) => {
    const n = safeNumber(value);
    const abs = Math.abs(n);

    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.?0+$/, "")}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.?0+$/, "")}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  
  const secondColor = useMemo(() => lighten(themeColor, 0.4), [themeColor]);
  // Force charts to remount when color (or title label) changes  
  const chartKey = useMemo(() => `${secondColor}-${compareRange}-${channel}`, [secondColor, compareRange, channel]);

  const currencySymbol = useMemo(() => {
    if (!isChannel) return "$";
    const cur = selectedCurrency || channel_orders_currency || "INR";
    if (cur === "GBP") return "£";
    if (cur === "INR") return "₹";
    return `${cur} `;
  }, [isChannel, selectedCurrency, channel_orders_currency]);

    // ---------------- Effects (centralized fetching) ----------------

    useEffect(() => {
    skipNextSearchResetRef.current = true;
    const params = new URLSearchParams(location.search);

    // Channel
    const ch = normalizeChannel(params.get("channel") || channel);
    if (ch !== channel) setChannel(ch);
    if (ch === "UK") dispatch(setChannelOrdersCurrency("GBP"));
    if (ch === "India") dispatch(setChannelOrdersCurrency("INR"));

    // Period & date range
    const p = params.get("period") || activePeriod || DEFAULT_PERIOD;
    if (p !== activePeriod) setActivePeriod(p);
    if (!orders_date_start || !orders_date_end) {
      const { startTime, endTime } = getPeriodRange(p);
      dispatch(setOrdersDateRange(startTime, endTime));
    }

    // Compare by
    const cmp = params.get("compare");
    if (cmp && cmp !== compareRange) setCompareRange(cmp);

    // Pagination
    const pg = Number(params.get("page")) || orders_page_current;
    const per = Number(params.get("per")) || orders_items_per_page;
    if (pg !== orders_page_current) dispatch(setOrdersPageCurrent(pg));
    if (per !== orders_items_per_page) dispatch(setOrdersItemsPerPage(per));

    // Search
    const q = params.get("q") || "";
    if (q && q !== searchTerm) {
      skipNextSearchResetRef.current = true;
      setSearchTerm(q);
    }

    // Products (all channels) restore applied + selected from URL
    // URL format: &products=12,15,99
    const productsCsv = params.get("products") || "";
    const productsFromUrl = productsCsv
      .split(",")
      .map((x) => String(x).trim())
      .filter(Boolean);

    if (productsFromUrl.length) {
      setSelectedProductIds(productsFromUrl);
      setAppliedProductIds(productsFromUrl);

      // bust dedupe so table+comparison refetch using restored products
      lastCompareKeyRef.current = "";
      lastTableKeyRef.current = "";
    }

    setTimeout(() => {
      hydratedRef.current = true;
    }, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return; // don’t run before initial read
    const desired = buildOrdersQuery();
    if (location.search !== desired) {
      navigate({ pathname: '/orders/channel', search: desired }, { replace: true });
    }
  // include everything that represents list state:
  }, [
    channel,
    activePeriod,
    compareRange,
    orders_page_current,
    orders_items_per_page,
    debouncedSearchTerm,
    appliedProductIds,
    navigate,
    location.search
  ]);

  // Debounce search (500ms) and reset page when the term changes
  useEffect(() => {
    const h = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      // Only reset to page 1 when user changed the search, not when we hydrated from URL
      if (skipNextSearchResetRef.current) {
        skipNextSearchResetRef.current = false; // consume the skip
      } else {
        dispatch(setOrdersPageCurrent(1));
      }
    }, 500);
    return () => clearTimeout(h);
  }, [searchTerm, dispatch]);


  // Compute fallback compare window (only when needed)
  const computeWindowFromCompare = useCallback((range) => {
    const now = new Date();
    const startOfLocalDay = (d) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };
    const today0 = startOfLocalDay(now);
    const toISO = (d) => new Date(d).toISOString();

    switch (range) {
      case 'day': {
        return { startTime: toISO(today0), endTime: toISO(now) };
      }
      case 'week': {
        const dow = (today0.getDay() + 6) % 7; // Mon=0..Sun=6
        const mon0 = new Date(today0);
        mon0.setDate(today0.getDate() - dow);
        mon0.setHours(0, 0, 0, 0);
        return { startTime: toISO(mon0), endTime: toISO(now) };
      }
      case 'month': {
        const m0 = new Date(today0.getFullYear(), today0.getMonth(), 1, 0, 0, 0, 0);
        return { startTime: toISO(m0), endTime: toISO(now) };
      }
      case 'year': {
        const y0 = new Date(today0.getFullYear(), 0, 1, 0, 0, 0, 0);
        return { startTime: toISO(y0), endTime: toISO(now) };
      }
      default:
        return { startTime: null, endTime: null };
    }
  }, []);

  // Mount: set default period (no fetch here; effects below handle it)
  // useEffect(() => {
  //   const { startTime, endTime } = getPeriodRange(DEFAULT_PERIOD);
  //   setActivePeriod(DEFAULT_PERIOD);
  //   dispatch(setOrdersDateRange(startTime, endTime));
  //   dispatch(setOrdersPageCurrent(1));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Comparison fetch (reacts to channel/compareRange + appliedUpcs)
  useEffect(() => {
    if (!hydratedRef.current) return; // ✅ prevent pre-hydration double fire

    const cur = isChannel
      ? (selectedCurrency || channel_orders_currency || "INR")
      : "USD";

    if (isShopmy) return; // ShopMy is a lens over barsys.com orders — no separate comparison
    if (channel === "US") {
      const upcsKey = appliedUpcs.length ? appliedUpcs.slice().sort().join(",") : "";
      const key = `${channel}|${compareRange}|${cur}|${upcsKey}`;
      if (lastCompareKeyRef.current === key) return;
      lastCompareKeyRef.current = key;

      dispatch(fetchOrdersComparison({ range: compareRange, upcs: appliedUpcs }));
    } else {
      const { startTime: start, endTime: end } = computeWindowFromCompare(compareRange);
      if (!start || !end) return;

      const key = `${channel}|${compareRange}|${start}|${end}|${cur}`;
      if (lastCompareKeyRef.current === key) return;
      lastCompareKeyRef.current = key;

      dispatch(
        fetchChannelOrdersComparison(
          { startTime: start, endTime: end, range: compareRange },
          cur,
          appliedSkus
        )
      );
    }
  }, [
    dispatch,
    channel,
    compareRange,
    computeWindowFromCompare,
    isChannel,
    selectedCurrency,
    channel_orders_currency,
    appliedUpcs,
    appliedSkus
  ]);


  // Table fetch (reacts to channel/period/page/perPage/search + appliedUpcs)
  useEffect(() => {
    if (!hydratedRef.current) return; // ✅ prevent pre-hydration double fire

    const cur = isChannel
      ? (selectedCurrency || channel_orders_currency || "INR")
      : "USD";

    if (channel === "US") {
      const upcsKey = appliedUpcs.length ? appliedUpcs.slice().sort().join(",") : "";
      const key = `${channel}|${activePeriod}|${orders_page_current}|${orders_items_per_page}|${debouncedSearchTerm}|${cur}|${upcsKey}|${fulfillmentFilter || ""}`;
      if (lastTableKeyRef.current === key) return;
      lastTableKeyRef.current = key;

      dispatch(
        fetchOrders(
          null,
          null,
          orders_page_current,
          orders_items_per_page,
          debouncedSearchTerm,
          activePeriod,
          appliedUpcs,
          fulfillmentFilter
        )
      );
    } else if (isShopmy) {
      const key = `${channel}|${activePeriod}|${orders_page_current}|${orders_items_per_page}|${debouncedSearchTerm}`;
      if (lastTableKeyRef.current === key) return;
      lastTableKeyRef.current = key;

      dispatch(
        fetchShopmyOrders(
          orders_page_current,
          orders_items_per_page,
          debouncedSearchTerm,
          activePeriod
        )
      );
    } else {
      const key = `${channel}|${effectiveStart}|${effectiveEnd}|${orders_page_current}|${orders_items_per_page}|${debouncedSearchTerm}|${cur}`;
      if (lastTableKeyRef.current === key) return;
      lastTableKeyRef.current = key;

      dispatch(
        fetchChannelOrders(
          effectiveStart,
          effectiveEnd,
          orders_page_current,
          orders_items_per_page,
          debouncedSearchTerm,
          null,
          cur,
          appliedSkus
        )
      );
    }
  }, [
    dispatch,
    channel,
    isChannel,
    selectedCurrency,
    channel_orders_currency,
    effectiveStart,
    effectiveEnd,
    orders_page_current,
    orders_items_per_page,
    debouncedSearchTerm,
    activePeriod,
    appliedUpcs,
    appliedSkus,
    fulfillmentFilter
  ]);

  // Leaving the US channel clears the fulfillment filter — the non-US fetch path has
  // no fulfillment param, and a filter that silently stops applying is worse than none.
  useEffect(() => {
    if (channel !== "US" && fulfillmentFilter !== null) setFulfillmentFilter(null);
  }, [channel, fulfillmentFilter]);

  // ---------------- Handlers (no fetch directly) ----------------

  const handleChannelChange = (e) => {
    const val = e.target.value; // "US" | "UK" | "India" | "SHAKERPRO"
    setChannel(val);
    if (val === "UK")    dispatch(setChannelOrdersCurrency("GBP"));
    if (val === "India") dispatch(setChannelOrdersCurrency("INR"));
    dispatch(setOrdersPageCurrent(1));
  };

  // SHAKERPRO fulfillment handlers
  const openFulfillModal = (order) => {
    setFulfillTarget(order);
    setFulfillTracking("");
    setFulfillCarrier("UPS");
    setFulfillError(null);
  };
  const closeFulfillModal = () => {
    setFulfillTarget(null);
    setFulfillError(null);
  };
  // Is this row owned by the engine rather than Saleor? `ownership` is the ONLY
  // discriminator — never `source`, which reads 'barsys.com' on mirrors AND on
  // engine-native orders, so keying on it would send engine orders to Saleor.
  const isEngineOwned = (o) => !!o && o.ownership === "engine" && !!o.engineOrderId;

  const submitFulfill = async () => {
    if (!fulfillTracking.trim()) { setFulfillError("Tracking number is required"); return; }

    // ENGINE-NATIVE: the order does not exist in Saleor at all, so the SALEOR_US
    // path below would look it up by number, find nothing, and report a missing
    // order. Route it at the engine, which fulfils AND attaches tracking in one go —
    // and the tracking step is what emails the customer and releases the PayPal hold.
    //
    // Carrier is deliberately not sent: the engine's TrackingInput is
    // { trackingNumber } only, so a carrier would be silently dropped and PayPal
    // would receive 'OTHER' regardless. The modal hides the carrier field for these
    // rows rather than collecting a value that goes nowhere.
    if (isEngineOwned(fulfillTarget)) {
      setFulfillError(null);
      // Carrier is now required for engine orders too: the engine's TrackingInput
      // accepts one, so PayPal gets the real carrier instead of falling through to
      // 'OTHER' and handing the buyer a tracking link that resolves to nothing.
      if (!fulfillCarrier.trim()) { setFulfillError("Carrier is required"); return; }
      const res = await dispatch(
        fulfillAndTrack(fulfillTarget.engineOrderId, fulfillTracking.trim(), fulfillCarrier.trim()),
      );
      if (res && res.ok) { closeFulfillModal(); return; }
      // Keep the modal open on failure so the message is attached to the action that
      // produced it. A partial failure (fulfilled but tracking failed) says so.
      setFulfillError((res && res.error) || "Fulfilment failed");
      return;
    }

    if (!fulfillCarrier.trim())  { setFulfillError("Carrier is required"); return; }
    const payload = {
      orderId: fulfillTarget.orderId,
      tracking: fulfillTracking.trim(),
      carrier: fulfillCarrier.trim(),
    };
    const isSaleorUs = fulfillTarget.channel === "SALEOR_US";
    const action = isSaleorUs ? fulfillSaleorUsOrder : fulfillShakerproOrder;
    dispatch(
      action(payload, (err, data) => {
        if (err) { setFulfillError(err); return; }
        closeFulfillModal();
        // Saleor fulfillments also push tracking to PayPal — surface anything
        // that needs a follow-up so the operator isn't flying blind.
        if (isSaleorUs && data && data.paypal && data.paypal !== "ok") {
          window.alert(`Order marked shipped in Saleor.\nPayPal tracking: ${data.paypal}`);
        }
      })
    );
  };

  const paginate = useCallback((page) => {
    dispatch(setOrdersPageCurrent(page));
  }, [dispatch]);

  const handlePerPage = (e) => {
    const pp = Number(e.target.value);
    dispatch(setOrdersItemsPerPage(pp));
    dispatch(setOrdersPageCurrent(1));
  };

  const handlePeriod = (key) => {
    setActivePeriod(key);
    const { startTime, endTime } = getPeriodRange(key);
    dispatch(setOrdersDateRange(startTime, endTime));
    dispatch(setOrdersPageCurrent(1));
  };

  

  // ---------------- Charts ----------------
  const selected = COMPARE_RANGES.find(r => r.value === compareRange);
  const rangeLabel = selected ? selected.label : "";
  const categories = [`Previous ${rangeLabel}`, `Current ${rangeLabel}`];

  const countSeries = [{ name: "Orders", data: [safeNumber(cmpData?.previousCount), safeNumber(cmpData?.currentCount)] }];
  const amountSeries = [
    { name: "Amount", data: [safeNumber(cmpData?.previousAmount), safeNumber(cmpData?.currentAmount)] }
  ];


  const baseOpts = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { distributed: true, columnWidth: "45%", borderRadius: 2 } },
    dataLabels: { enabled: true, style: { fontSize: '12px', colors: ['#fff'] }, offsetY: -6 },
    xaxis: { categories, labels: { rotate: -15, style: { fontSize: '13px' } } },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    tooltip: { y: { formatter: v => v.toLocaleString() } },
    responsive: [
      { breakpoint: 992, options: { plotOptions: { bar: { columnWidth: '55%' } }, title: { style: { fontSize: '14px' } }, xaxis: { labels: { rotate: -10, style: { fontSize: '12px' } } } } },
      { breakpoint: 576, options: { plotOptions: { bar: { columnWidth: '70%' } }, title: { style: { fontSize: '12px' } }, xaxis: { labels: { rotate: 0, style: { fontSize: '11px' } } }, dataLabels: { style: { fontSize: '10px' } } } }
    ]
  };

  const countOpts = {
    ...baseOpts,
    colors: [themeColor, secondColor],
    plotOptions: { bar: { dataLabels: { position: "top" } } },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: { fontFamily: "Chakra Petch", fontSize: "10px", fontWeight: "600", colors: [themeColor] },
    },
    title: { text: `Orders Count Comparison (${rangeLabel})`, align: 'center', style: { fontSize: '16px', fontWeight: '600', fontFamily: "Chakra Petch", color: secondColor } },
    xaxis: { categories, labels: { rotate: 45, style: { fontFamily: "Chakra Petch", fontSize: "12px", colors: [themeColor] } } },
    yaxis: { labels: { style: { fontFamily: "Chakra Petch", fontSize: "12px", colors: [themeColor] } } },
  };

  const amountOpts = {
    ...baseOpts,
    colors: [themeColor, secondColor],
    plotOptions: { bar: { dataLabels: { position: "top" } } },

    dataLabels: {
      enabled: true,
      offsetY: -20,
      formatter: (val) => formatCurrencyCompact(val),
      style: { fontFamily: "Chakra Petch", fontSize: "10px", fontWeight: "600", colors: [themeColor] },
    },

    tooltip: {
      y: { formatter: (v) => formatCurrencyCompact(v) },
    },

    yaxis: {
      labels: {
        style: { fontFamily: "Chakra Petch", fontSize: "12px", colors: [themeColor] },
        // optional: if you want the axis to be compact too (no symbol or with symbol)
        formatter: (val) => formatNumberAbbrev(val),
        // if you want the axis to include currency symbol too, use:
        // formatter: (val) => formatCurrencyCompact(val),
      },
    },

    title: {
      text: `Orders Amount Comparison (${rangeLabel})`,
      align: "center",
      style: { fontSize: "16px", fontWeight: "600", fontFamily: "Chakra Petch", color: secondColor },
    },

    xaxis: { categories, labels: { rotate: 45, style: { fontFamily: "Chakra Petch", fontSize: "12px", colors: [themeColor] } } },
  };


  // ---------------- Pagination math ----------------
  const startIndex = tableTotal ? (orders_page_current - 1) * orders_items_per_page + 1 : 0;
  const endIndex   = Math.min(orders_page_current * orders_items_per_page, tableTotal);

  //-----Channel Aware Order Details Link ---//
  // Merged US rows carry order.channel ('US' | 'SHAKERPRO') so shakerpro
  // orders route to their own details endpoint.
  const detailsHrefFor = useCallback((order) => {
    const orderId = typeof order === 'object' && order !== null ? order.orderId : order;
    const rowChannel = typeof order === 'object' && order !== null ? order.channel : null;
    if (rowChannel === 'SHAKERPRO' || rowChannel === 'COLLINS') return `/orders/order-details/shakerpro/${orderId}`;
    if (rowChannel === 'SALEOR_US') return `/orders/order-details/saleor-us/${orderId}`;
    if (rowChannel === 'SHOPMY') return `/orders/order-details/us/${orderId}`;
    const ch = (channel || 'US').toLowerCase();
    return ch === 'us'
      ? `/orders/order-details/us/${orderId}`
      : `/orders/order-details/${ch}/${orderId}`;
  }, [channel]);


  //helper for keeping Params when coming back
const buildOrdersQuery = () => {
  const params = new URLSearchParams();
  params.set("channel", channel);
  params.set("period", activePeriod);
  params.set("compare", compareRange);
  params.set("page", String(orders_page_current));
  params.set("per", String(orders_items_per_page));
  if (debouncedSearchTerm) params.set("q", debouncedSearchTerm);

  // ✅ persist applied products (productIds), not UPCs
  if (appliedProductIds?.length) {
    params.set("products", appliedProductIds.map(String).join(","));
  }

  return `?${params.toString()}`;
};


  // ---------------- Render ----------------
return (
  <div>
    {/* Header + Channel selector */}
    <div className="mb-3">
      <div className="row g-3 align-items-start align-items-xl-center">
        {/* Left: page title */}
        <div className="col-12 col-xl-auto">
          <div>
            <ul className="breadcrumb">
              <h6><li className="breadcrumb-item active">PAGES /</li></h6>
              <h6><li className="breadcrumb-item">&nbsp;ORDERS</li></h6>
            </ul>
            <h1 className="page-header mb-0">ORDERS</h1>
          </div>
        </div>

        {/* Center: Product multi-select dropdown */}
        <div className="col-12 col-xl">
          <div
            id="productsMultiSelectDropdown"
            className="d-flex flex-wrap flex-xl-nowrap align-items-center gap-2 position-relative mx-xl-auto"
            style={{ maxWidth: 980 }}
          >
            <label className="mb-0" style={{ whiteSpace: "nowrap" }}>
              Products:
            </label>

            {/* Controls row: dropdown grows, button fixed */}
            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
              <div className="dropdown flex-grow-1" style={{ minWidth: 0 }}>
                <button
                  type="button"
                  className="form-control text-start d-flex align-items-center justify-content-between"
                  onClick={() => setProductsDropdownOpen((v) => !v)}
                  disabled={loading_analytics_product_options}
                  aria-expanded={productsDropdownOpen}
                  style={{ cursor: "pointer" }}
                  title="Select one or more products"
                >
                  <span className="text-truncate" style={{ minWidth: 0 }}>
                    {loading_analytics_product_options ? "Loading…" : selectedProductsLabel}
                  </span>
                  <i className={`fa fa-chevron-${productsDropdownOpen ? "up" : "down"} ms-2`} />
                </button>

                {productsDropdownOpen && (
                  <div
                    className="dropdown-menu show p-2"
                    style={{
                      width: "100%",
                      maxHeight: 280,
                      overflowY: "auto",
                      zIndex: 2000,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between px-2 pb-2">
                      <div className="small text-muted">
                        {selectedProductIds.length ? `${selectedProductIds.length} selected` : "None selected"}
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          clearSelectedProducts();
                        }}
                        disabled={!selectedProductIds.length}
                      >
                        Clear
                      </button>
                    </div>

                    <div className="dropdown-divider my-1" />

                    {analytics_product_options_error ? (
                      <div className="text-danger small px-2 py-2">{analytics_product_options_error}</div>
                    ) : (analytics_product_options || []).length === 0 ? (
                      <div className="text-muted small px-2 py-2">No products found</div>
                    ) : (
                      (analytics_product_options || []).map((opt) => {
                        const id = String(opt.value);
                        const checked = selectedProductIds.includes(id);

                        return (
                          <div
                            key={id}
                            role="menuitemcheckbox"
                            aria-checked={checked}
                            className="dropdown-item d-flex align-items-center gap-2"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleProductId(id);
                            }}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                width: 16,
                                height: 16,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 3,
                                border: `1px solid ${checked ? themeColor : "rgba(255,255,255,0.35)"}`,
                                background: checked ? themeColor : "transparent",
                                boxShadow: checked ? `0 0 0 2px ${themeColor}33` : "none",
                                flex: "0 0 auto",
                              }}
                            >
                              {checked ? (
                                <i className="fa fa-check" style={{ color: "#fff", fontSize: 11, lineHeight: 1 }} />
                              ) : null}
                            </span>

                            <span className="flex-grow-1">{opt.label}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleProductsQueryClick}
                disabled={!hasSelectionChanges}
                title={hasSelectionChanges ? "Apply selected products" : "No changes to apply"}
                style={{
                  height: 38,
                  width: 44,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto",
                }}
              >
                <i className="fas fa-search" />
              </button>
            </div>

            {analytics_product_options_error ? (
              <span className="text-danger small d-none d-xl-inline" style={{ whiteSpace: "nowrap" }}>
                {analytics_product_options_error}
              </span>
            ) : null}
          </div>

          {/* On small screens show the error below (prevents overflow in the header row) */}
          {analytics_product_options_error ? (
            <div className="text-danger small d-xl-none mt-1">{analytics_product_options_error}</div>
          ) : null}
        </div>

        {/* Right: Sales Channel */}
        <div className="col-12 col-xl-auto">
          <div className="d-flex align-items-center gap-2 justify-content-xl-end">
            <label className="mb-0 me-2" style={{ whiteSpace: "nowrap" }}>
              Sales Channel:
            </label>
            <select className="form-select w-auto" value={channel} onChange={handleChannelChange}>
              <option value="US">US</option>
              {/* <option value="UK">UK</option> */}
              <option value="India">India</option>
              <option value="ShopMy">ShopMy (creators)</option>
            </select>
          </div>
        </div>
      </div>
    </div>


    {/* Comparison Card */}
    <Card className="mb-4">
      <div className="d-flex justify-content-center align-items-center mb-3" style={{ marginTop: 10 }}>
        <label className="me-2 mb-0" style={{ fontWeight: 500 }}>
          Compare By:
        </label>
        <select className="form-select w-auto" value={compareRange} onChange={(e) => setCompareRange(e.target.value)}>
          {COMPARE_RANGES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="row px-4 pb-4">
        <div className="col-md-6">
          {cmpLoading ? (
            <div className="text-center py-5">Loading…</div>
          ) : cmpError ? (
            <div className="text-danger text-center py-5">{cmpError}</div>
          ) : (
            <Chart key={`count-${chartKey}`} options={countOpts} series={countSeries} type="bar" height={250} />
          )}
        </div>

        <div className="col-md-6">
          {cmpLoading ? (
            <div className="text-center py-5">Loading…</div>
          ) : cmpError ? (
            <div className="text-danger text-center py-5">{cmpError}</div>
          ) : (
            <Chart key={`amount-${chartKey}`} options={amountOpts} series={amountSeries} type="bar" height={250} />
          )}
        </div>
      </div>
    </Card>

    {/* Period buttons */}
    <div className="row mb-3">
      <div className="col-xl-12">
        {(() => {
          const isThemeBlack = typeof document !== "undefined" && document.body.classList?.contains("theme-black");
          if (isThemeBlack) {
            return (
              <div className="d-flex flex-wrap gap-2 theme-black-controls">
                {PERIODS.map(({ label, key }) => (
                  <button
                    key={key}
                    type="button"
                    className={`btn freq-btn${activePeriod === key ? " active is-active" : ""}`}
                    aria-pressed={activePeriod === key}
                    onClick={() => handlePeriod(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            );
          }
          return (
            <div className="d-flex flex-wrap gap-2">
              {PERIODS.map(({ label, key }) => (
                <button
                  key={key}
                  className={`btn ${activePeriod === key ? "btn-theme" : "btn-secondary"}`}
                  onClick={() => handlePeriod(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          );
        })()}
      </div>
    </div>

    {/* Table */}
    <Card>
      {/* Fulfillment filter tabs.
          HISTORY: these were Bootstrap data-bs-toggle="tab" anchors pointing at panes
          (#unfulfilledTab etc.) that were NEVER BUILT — the page has exactly one pane,
          #allTab. Clicking any tab but All hid that pane and rendered a blank screen,
          and no filtering of any kind happened (verified in the live DOM, 2026-08-05).
          NOW: React state drives the filter, the single pane is always visible, and the
          filtering is SERVER-SIDE via ?fulfillmentStatus= (see fulfillmentFilter above).
          Unpaid / Open / Closed / Local delivery were part of the original stub bar and
          have no backend semantics yet — kept visually but inert (they can no longer
          blank the page). Wire each only when its backend filter actually exists. */}
      <ul className="nav nav-tabs nav-tabs-v2 px-4">
        {/* The filter rides the US table fetch (paginated-by-range-by-upc). Non-US
            channels fetch via fetchChannelOrders, which has no fulfillment param — so
            only All is offered there, rather than tabs that highlight but do nothing. */}
        {(channel === "US"
          ? [
              { label: "All", value: null },
              { label: "Unfulfilled", value: "Unfulfilled" },
              { label: "Fulfilled", value: "Fulfilled" },
            ]
          : [{ label: "All", value: null }]
        ).map(({ label, value }) => (
          <li className="nav-item me-3" key={label}>
            <a
              href="#allTab"
              className={
                "nav-link px-2" + (fulfillmentFilter === value ? " active" : "")
              }
              onClick={(e) => {
                e.preventDefault();
                if (fulfillmentFilter === value) return;
                setFulfillmentFilter(value);
                dispatch(setOrdersPageCurrent(1)); // filtered result set starts at page 1
              }}
            >
              {label}
            </a>
          </li>
        ))}
        {["Unpaid", "Open", "Closed", "Local delivery"].map((label) => (
          <li className="nav-item me-3" key={label}>
            <a
              href="#allTab"
              className="nav-link px-2 disabled"
              aria-disabled="true"
              title="Not wired to a backend filter yet"
              onClick={(e) => e.preventDefault()}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>

      <div className="tab-content p-4">
        <div className="tab-pane fade show active" id="allTab">
          <div className="input-group mb-4">
            <div className="flex-fill position-relative">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control px-35px"
                  placeholder="Filter Orders ( Min. 3 Characters )"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div
                  className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                  style={{ zIndex: 1020 }}
                >
                  <i className="fa fa-search opacity-5"></i>
                </div>
              </div>
            </div>

            {/* Static dropdowns (visual only) */}
            <button className="btn btn-outline-default dropdown-toggle rounded-0" type="button" data-bs-toggle="dropdown">
              <span className="d-none d-md-inline">Payment Status</span>
              <span className="d-inline d-md-none">
                <i className="fa fa-credit-card"></i>
              </span>{" "}
              &nbsp;
            </button>
            <div className="dropdown-menu">
              <a className="dropdown-item" href="#/">
                Paid
              </a>
              <a className="dropdown-item" href="#/">
                Pending
              </a>
              <a className="dropdown-item" href="#/">
                Partially refunded
              </a>
              <div role="separator" className="dropdown-divider"></div>
              <a className="dropdown-item" href="#/">
                All
              </a>
            </div>

            <button className="btn btn-outline-default dropdown-toggle" type="button" data-bs-toggle="dropdown">
              <span className="d-none d-md-inline">Fulfillment status</span>
              <span className="d-inline d-md-none">
                <i className="fa fa-check"></i>
              </span>{" "}
              &nbsp;
            </button>
            <div className="dropdown-menu dropdown-menu-end">
              <a className="dropdown-item" href="#/">
                Fulfilled
              </a>
              <a className="dropdown-item" href="#/">
                Unfulfilled
              </a>
              <a className="dropdown-item" href="#/">
                Partially fulfilled
              </a>
              <div role="separator" className="dropdown-divider"></div>
              <a className="dropdown-item" href="#/">
                All
              </a>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover text-nowrap">
              <thead>
                <tr>
                  <th className="border-top-0 pt-0 pb-2"></th>
                  <th className="border-top-0 pt-0 pb-2">Order</th>
                  <th className="border-top-0 pt-0 pb-2">Source</th>
                  <th className="border-top-0 pt-0 pb-2">Date</th>
                  <th className="border-top-0 pt-0 pb-2">Customer</th>
                  <th className="border-top-0 pt-0 pb-2">Total</th>
                  <th className="border-top-0 pt-0 pb-2">Payment</th>
                  <th className="border-top-0 pt-0 pb-2">Payment status</th>
                  <th className="border-top-0 pt-0 pb-2">Fulfillment status</th>
                  <th className="border-top-0 pt-0 pb-2">Items</th>
                  <th className="border-top-0 pt-0 pb-2">Products</th>
                  <th className="border-top-0 pt-0 pb-2">Delivery method</th>
                  {isShopmy && (
                    <>
                      <th className="border-top-0 pt-0 pb-2">Creator</th>
                      <th className="border-top-0 pt-0 pb-2">Commission</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan="12" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : tableError ? (
                  <tr>
                    <td colSpan="12" className="text-danger text-center">
                      {tableError}
                    </td>
                  </tr>
                ) : tableOrders && tableOrders.length > 0 ? (
                  tableOrders.map((order, idx) => (
                    <tr key={order.orderId || idx}>
                      <td className="w-10px align-middle">
                        <div className="form-check">
                          <input type="checkbox" className="form-check-input" id={`orderCheck${idx}`} />
                          <label className="form-check-label" htmlFor={`orderCheck${idx}`}></label>
                        </div>
                      </td>
                      <td className="align-middle">
                        <Link
                          to={{
                            pathname: detailsHrefFor(order),
                            search: buildOrdersQuery(),
                          }}
                        >
                          #{order.number || order.orderId}
                        </Link>
                        {order.number && !String(order.number).endsWith(String(order.orderId)) ? (
                          <div className="small text-body text-opacity-50">{order.orderId}</div>
                        ) : null}
                      </td>
                      <td className="align-middle">
                        {/* SOURCE — the real origin of the purchase. Prefers
                            order.sourceKey (the commerce `orders.source` domain, e.g.
                            coaster.barsys.com) and falls back to the old channel-literal
                            rendering when the backend ships no sourceKey for the row.
                            All of that logic is in ./orderSource.js, including why the
                            fallback has to exist. */}
                        {(() => {
                          const src = resolveOrderSource(order, channel);
                          return (
                            <span
                              className={
                                "badge border px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center " +
                                (src.accent ? "border-theme text-theme" : "border-gray-500 text-gray-500")
                              }
                              title={src.title}
                            >
                              {src.label}
                            </span>
                          );
                        })()}
                        {/* ShopMy ATTRIBUTION on an ordinary row. The primary badge
                            above keeps naming the store the sale actually happened in,
                            because a ShopMy order IS a barsys.com order — ShopMy
                            attributes existing sales rather than being its own channel,
                            which is why its rows are never merged into the totals. This
                            second badge is what makes creator-driven orders visible in
                            the normal list. Fields come from the backend annotation
                            (shopmyCreator / shopmyCommission). */}
                        {order.shopmyCreator ? (
                          <>
                            <span
                              className="badge border border-theme text-theme px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center ms-1"
                              title={
                                "Attributed by ShopMy to " + order.shopmyCreator +
                                (order.shopmyCommission != null ? ` — $${order.shopmyCommission} commission` : "") +
                                (order.shopmyPromoCode ? ` — code ${order.shopmyPromoCode}` : "")
                              }
                            >
                              ShopMy
                            </span>
                            <div className="small text-body text-opacity-50 mt-1">
                              {order.shopmyCreatorUrl ? (
                                <a href={order.shopmyCreatorUrl} target="_blank" rel="noreferrer">
                                  {order.shopmyCreator}
                                </a>
                              ) : (
                                order.shopmyCreator
                              )}
                              {order.shopmyCommission != null ? ` · $${order.shopmyCommission}` : ""}
                            </div>
                          </>
                        ) : null}
                      </td>
                      <td className="align-middle">{order.date ? formatDbDateNoTz(order.date) : ""}</td>
                      <td className="align-middle">{order.customer}</td>
                      <td className="align-middle">{order.total != null ? `${currencySymbol}${order.total}` : ""}</td>
                      <td className="align-middle text-capitalize">
                        {(order.paymentMethod || "").replace(/_/g, " ") || "—"}
                      </td>
                      <td className="py-1 align-middle">
                        <span
                          className={
                            "badge border px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center " +
                            (order.paymentStatus === "Paid"
                              ? "border-success text-success"
                              : order.paymentStatus === "Pending"
                              ? "border-warning text-warning"
                              : "border-gray-300 text-gray-300")
                          }
                        >
                          <i className="fa fa-circle fs-9px fa-fw me-5px"></i> {order.paymentStatus}
                        </span>
                      </td>
                      <td className="align-middle">
                        <span
                          className={
                            "badge border px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center " +
                            ((order.fulfillmentStatus || "Fulfilled") === "Fulfilled"
                              ? "border-success text-success"
                              : order.fulfillmentStatus === "Unfulfilled"
                              ? "border-warning text-warning"
                              : order.fulfillmentStatus === "Ready for pickup"
                              ? "border-primary text-primary"
                              : order.fulfillmentStatus === "Partially fulfilled"
                              ? "border-danger text-danger"
                              : "border-gray-300 text-gray-300")
                          }
                        >
                          <i className="fa fa-circle fs-9px fa-fw me-5px"></i> {order.fulfillmentStatus || "Fulfilled"}
                        </span>
                        {(order.channel === "SHAKERPRO" || order.channel === "SALEOR_US" || order.channel === "COLLINS") && order.fulfillmentStatus !== "Fulfilled" && (
                          <button
                            type="button"
                            className="btn btn-outline-theme btn-sm ms-2 py-0"
                            disabled={fulfilling_shakerpro_order}
                            onClick={() => openFulfillModal(order)}
                          >
                            Mark shipped
                          </button>
                        )}
                        {(order.channel === "SHAKERPRO" || order.channel === "SALEOR_US" || order.channel === "COLLINS") && order.fulfillmentStatus === "Fulfilled" && order.tracking ? (
                          <div className="small text-body text-opacity-50 mt-1">
                            {order.carrier ? `${order.carrier} · ` : ""}{order.tracking}
                          </div>
                        ) : null}
                        {order.channel === "SHAKERPRO" && order.fulfillmentStatus !== "Fulfilled" && order.expectedShipDate ? (
                          <div className="small text-body text-opacity-50 mt-1">
                            est. ship {order.expectedShipDate}
                          </div>
                        ) : null}
                      </td>
                      <td className="align-middle">{order.items ? `${order.items} items` : ""}</td>
                      {/* WHAT was bought, not just how many. `items` is a quantity
                          count, so finding "the Shaker Pro order" meant opening each
                          row. Titles come from the API (productSummary), already
                          capped at 5 with the remainder reported rather than dropped. */}
                      {/* Products cell. Each line renders as "Coaster 2.0 — Black"
                          when the product has a colour variant, and "Barsys Shaker
                          Pro" (unchanged) when it doesn't. The colour comes from
                          p.variantName (engine order_line.variant_name / Saleor
                          line.variant.name), populated by orders.service.js on the
                          API side. Attribution-only rows (ShopMy, Shakerpro,
                          Collins) leave products[] empty and still render as an
                          em-dash — the outer conditional below handles that. */}
                      <td className="align-middle" style={{ maxWidth: 260 }}>
                        {Array.isArray(order.products) && order.products.length ? (
                          <span className="small">
                            {order.products.map((p, i) => (
                              <span key={i} className="d-block text-truncate" title={`${p.title || p.sku || ''}${p.variantName ? ' — ' + p.variantName : ''}${p.sku ? ' · ' + p.sku : ''}`}>
                                {p.quantity > 1 ? `${p.quantity} × ` : ""}{p.title || p.sku}{p.variantName ? ` — ${p.variantName}` : ""}
                              </span>
                            ))}
                            {order.productsTruncated > 0 ? (
                              <span className="text-inverse text-opacity-50">+{order.productsTruncated} more</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-inverse text-opacity-25 small">—</span>
                        )}
                      </td>
                      <td className="align-middle">{order.deliveryMethod || "Standard"}</td>
                      {isShopmy && (
                        <>
                          <td className="align-middle">
                            {order.creatorUrl ? (
                              <a href={order.creatorUrl} target="_blank" rel="noreferrer">{order.creator || "—"}</a>
                            ) : (order.creator || "—")}
                            {order.code ? (
                              <div className="small text-body text-opacity-50">code {order.code}</div>
                            ) : null}
                          </td>
                          <td className="align-middle">
                            {order.commission != null ? `$${order.commission}` : "—"}
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer: info (left) + per-page + pagination (right) */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="me-auto text-center text-md-start mb-2 mb-md-0">
              {tableTotal > 0 ? (
                <>
                  Showing{" "}
                  <span className="fw-bold" style={{ color: themeColor }}>
                    {startIndex}
                  </span>{" "}
                  to{" "}
                  <span className="fw-bold" style={{ color: themeColor }}>
                    {endIndex}
                  </span>{" "}
                  of{" "}
                  <span className="fw-bold" style={{ color: themeColor }}>
                    {tableTotal}
                  </span>{" "}
                  entries
                </>
              ) : (
                <>No entries to display</>
              )}
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto">
              <div className="d-flex">
                {tableTotal > 0 ? (
                  <Pagination
                    currentPage={orders_page_current}
                    itemsPerPage={orders_items_per_page}
                    totalItems={tableTotal}
                    paginate={(page) => paginate(page)}
                  />
                ) : (
                  <ul className="pagination mb-0 justify-content-end">
                    <li className="page-item disabled">
                      <span className="page-link">&lt;&lt;</span>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">&lt;</span>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">&gt;</span>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">&gt;&gt;</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>

    {/* SHAKERPRO "Mark shipped" modal — writes back to the shakerpro feed origin */}
    {fulfillTarget && (
      <>
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Mark order shipped</h5>
                <button type="button" className="btn-close" onClick={closeFulfillModal}></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Order <strong>#{fulfillTarget.number || fulfillTarget.orderId}</strong>
                  {fulfillTarget.customer ? <> — {fulfillTarget.customer}</> : null}
                </p>
                {isEngineOwned(fulfillTarget) && (
                  <div className="alert alert-info py-2 small mb-2">
                    Engine order — fulfilled directly in the commerce engine. Saving the
                    tracking number emails the customer and releases the PayPal hold.
                    Carrier is sent to PayPal with the tracking number.
                  </div>
                )}
                {!isEngineOwned(fulfillTarget) && fulfillTarget.channel === "SALEOR_US" && (
                  <p className="small text-body text-opacity-50">
                    Marks the order fulfilled in Saleor (customer gets the shipping email)
                    and uploads the tracking number to PayPal.
                  </p>
                )}
                <div className="mb-3">
                  <label className="form-label">Tracking number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={fulfillTracking}
                    onChange={(e) => setFulfillTracking(e.target.value)}
                    placeholder="1Z999AA10123456784"
                    autoFocus
                  />
                </div>
                {/* Shown for every source now. The engine's TrackingInput accepts a
                    carrier, so this value reaches PayPal instead of falling through to
                    'OTHER' — which is what made a buyer's tracking link resolve to
                    nothing. It was hidden for engine rows while the field could not be
                    conveyed; collecting a value that goes nowhere is worse than not
                    offering it. */}
                <div className="mb-3">
                    <label className="form-label">Carrier</label>
                    <select
                      className="form-select"
                      value={fulfillCarrier}
                      onChange={(e) => setFulfillCarrier(e.target.value)}
                    >
                      <option value="UPS">UPS</option>
                      <option value="USPS">USPS</option>
                      <option value="FedEx">FedEx</option>
                      <option value="DHL">DHL</option>
                      <option value="Other">Other</option>
                  </select>
                </div>
                {fulfillError && <div className="text-danger small">{fulfillError}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-default" onClick={closeFulfillModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-theme"
                  disabled={fulfilling_shakerpro_order}
                  onClick={submitFulfill}
                >
                  {fulfilling_shakerpro_order ? "Saving…" : "Confirm shipment"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    )}
  </div>
);

}

export default PagesOrders;
